import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import FetchArticles from "../utils/fetcharticle";
import { URLimage, UrlCategorie, UrlPeintureDigital, UrlTheme } from "../utils/varaibleFetch";

type Oeuvre = {
  id: number;
  name: string;
  image: string;
  description?: string;
  categorie?: string | { "@id"?: string; id?: number };
  theme?: string | { "@id"?: string; id?: number };
};

type FilterOption = {
  id: string;
  label: string;
};

type RelationValue = string | { "@id"?: string; id?: number; name?: string; nom?: string; title?: string } | undefined;

const getRelationId = (relation: RelationValue) => {
  if (!relation) return "";

  if (typeof relation === "string") {
    return relation.split("/").filter(Boolean).pop() || relation;
  }

  if (relation["@id"]) {
    return relation["@id"].split("/").filter(Boolean).pop() || relation["@id"];
  }

  return relation.id ? String(relation.id) : "";
};

const getRelationLabel = (relation: RelationValue) => {
  if (!relation || typeof relation === "string") return "";

  return relation.name || relation.nom || relation.title || "";
};

const formatFallbackLabel = (prefix: string, id: string) => `${prefix} ${id}`;

const fetchFilterOptions = async (url: string, prefix: string): Promise<FilterOption[]> => {
  const response = await axios.get(url);
  const items = response.data["hydra:member"] || response.data.member || response.data || [];

  return items.map((item: RelationValue) => {
    const id = getRelationId(item);
    return {
      id,
      label: getRelationLabel(item) || formatFallbackLabel(prefix, id),
    };
  }).filter((item: FilterOption) => item.id);
};

const Oeuvres: React.FC = () => {
  const { articles, isLoading } = FetchArticles(UrlPeintureDigital);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [themeFilter, setThemeFilter] = useState("");
  const [categoryOptions, setCategoryOptions] = useState<FilterOption[]>([]);
  const [themeOptions, setThemeOptions] = useState<FilterOption[]>([]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [categories, themes] = await Promise.all([
          fetchFilterOptions(UrlCategorie, "Categorie"),
          fetchFilterOptions(UrlTheme, "Theme"),
        ]);
        setCategoryOptions(categories);
        setThemeOptions(themes);
      } catch (error) {
        console.error("Erreur lors de la récupération des filtres :", error);
      }
    };

    fetchFilters();
  }, []);

  const categoryLabelById = useMemo(() => new Map(categoryOptions.map((option) => [option.id, option.label])), [categoryOptions]);

  const filteredArticles = useMemo(() => {
    return articles.filter((oeuvre: Oeuvre) => {
      const categoryId = getRelationId(oeuvre.categorie);
      const themeId = getRelationId(oeuvre.theme);

      return (!categoryFilter || categoryId === categoryFilter) && (!themeFilter || themeId === themeFilter);
    });
  }, [articles, categoryFilter, themeFilter]);

  return (
    <section className="all-artworks-page">
      <div className="all-artworks-page__container">
        <div className="all-artworks-page__filters" aria-label="Filtres des oeuvres">
          <label className="all-artworks-page__filter">
            <span>Categorie</span>
            <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
              <option value="">Toutes</option>
              {categoryOptions.map((category) => (
                <option value={category.id} key={category.id}>{category.label}</option>
              ))}
            </select>
          </label>

          <label className="all-artworks-page__filter">
            <span>Theme</span>
            <select value={themeFilter} onChange={(event) => setThemeFilter(event.target.value)}>
              <option value="">Tous</option>
              {themeOptions.map((theme) => (
                <option value={theme.id} key={theme.id}>{theme.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="all-artworks-page__grid" aria-label="Toutes les œuvres">
          {isLoading
            ? Array.from({ length: 7 }).map((_, index) => (
                <article className="all-artworks-page__item all-artworks-page__item--loading" key={`oeuvre-loading-${index}`} />
              ))
            : filteredArticles.map((oeuvre: Oeuvre) => (
                <article className="all-artworks-page__item" key={oeuvre.id}>
                  <Link className="all-artworks-page__item-inner focus-ring" to={`/oeuvre/${oeuvre.id}`}>
                    <img src={`${URLimage}/${oeuvre.image}`} alt={oeuvre.name} />
                    <div className="all-artworks-page__overlay" />
                    <div className="all-artworks-page__info">
                      <h2 className="all-artworks-page__title">{oeuvre.name}</h2>
                      <p className="all-artworks-page__meta">{categoryLabelById.get(getRelationId(oeuvre.categorie)) || "Oeuvre"} · œuvre originale</p>
                    </div>
                  </Link>
                </article>
              ))}
        </div>
      </div>
    </section>
  );
};

export default Oeuvres;
